<template>
  <div class="asset-category-tree">
    <!-- 「全部」是总览，名字停在最外层不缩进；右侧补两个跟分类行等宽的图标占位，
         好让三行的数量对齐成一列 -->
    <div class="act-fixed-item" :class="{ 'is-active': value === undefined }" @click="selectAll">
      <span class="act-name">全部</span>
      <span class="act-count">{{ totalAll }}</span>
      <span v-if="editable" class="act-edit-icon is-space" aria-hidden="true">
        <i class="el-icon-edit"></i>
      </span>
      <span v-if="editable" class="act-edit-icon is-space" aria-hidden="true">
        <i class="el-icon-edit"></i>
      </span>
    </div>
    <!-- 「未分类」和下面的各个顶级分组是同一层的兄弟，所以行内结构要跟分类行逐格对上：
         行首补一个拖动手柄占位，否则分类名会比「未分类」多缩进一个图标的宽度 -->
    <div class="act-fixed-item" :class="{ 'is-active': value === 0 }" @click="selectUncategorized">
      <i v-if="editable" class="act-drag-handle is-space el-icon-rank" aria-hidden="true"></i>
      <span class="act-name">未分类</span>
      <span class="act-count">{{ uncategorizedCount }}</span>
      <span v-if="editable" class="act-edit-icon is-space" aria-hidden="true">
        <i class="el-icon-edit"></i>
      </span>
      <span v-if="editable" class="act-edit-icon is-space" aria-hidden="true">
        <i class="el-icon-edit"></i>
      </span>
    </div>

    <!-- 顶级分支（.act-list 是滚动容器）；子级由 Branch 自己递归 -->
    <AssetCategoryBranch
      :categories="categories"
      :parent-id="null"
      :depth="1"
      :value="value"
      :editable="editable"
      :collapsed="collapsed"
      :reordering="reordering"
      @select="selectCategory"
      @toggle="toggleExpanded"
      @command="handleCommand"
      @reorder="onReorder"
    />

    <div v-if="editable" class="act-create-bar">
      <el-button type="text" icon="el-icon-plus" size="mini" class="act-create-btn" @click="handleCreate()">
        新建一级分组
      </el-button>
    </div>

    <!-- 换父（spec §6.3、派生点 c）。嵌在素材管理弹窗里，必须 append-to-body（同 AssetLibrary 的移动弹窗） -->
    <el-dialog title="移动分组" :visible.sync="moveVisible" width="360px" append-to-body custom-class="act-move-dialog">
      <div class="act-move-title">把「{{ moveSource ? moveSource.name : '' }}」移到：</div>
      <el-select v-model="moveTargetId" placeholder="请选择目标分组" class="act-move-select">
        <el-option v-for="o in moveOptions" :key="o.value" :value="o.value" :label="o.label" />
      </el-select>
      <div v-if="moveSource && moveOptions.length === 0" class="act-move-hint">没有可以移入的分组</div>
      <span slot="footer">
        <el-button @click="moveVisible = false">取消</el-button>
        <el-button type="primary" :loading="moving" :disabled="moveTargetId === undefined" @click="confirmMove">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
// $prompt 的类型签名跟 $confirm/$alert 共用同一个 Promise<MessageBoxData>（MessageBoxData 是
// MessageBoxInputData | 'confirm' | 'cancel' | 'close' 的联合），TS 分不出 $prompt 实际总是
// resolve 成带 value 字段的那支，要读 .value 就得显式转成 MessageBoxInputData
import type { MessageBoxInputData } from 'element-ui/types/message-box'
import type { AssetKind, AssetScope, AssetCategoryView } from '@/api/asset'
import { createAssetCategory, updateAssetCategory, deleteAssetCategory, reorderAssetCategories } from '@/api/asset'
import {
  TOP_LEVEL_TARGET,
  categoryIsLeaf,
  categoryPathLabel,
  categorySubtreeIds,
  canAddChild,
  reparentTargets
} from '@/utils/assetCategoryHierarchy'
import type { ReparentTarget } from '@/utils/assetCategoryHierarchy'
import AssetCategoryBranch from './AssetCategoryBranch.vue'

type RowCommand = 'child' | 'rename' | 'move' | 'delete'

@Component({ components: { AssetCategoryBranch } })
export default class AssetCategoryTree extends Vue {
  @Prop({ required: true }) readonly categories!: AssetCategoryView[]
  @Prop({ required: true }) readonly uncategorizedCount!: number
  @Prop({ required: true }) readonly totalAll!: number
  // undefined = 全部, 0 = 未分类, 其他 = 分类 id；不用 v-model 语法糖，父组件的 selectCategory
  // 还要做「同值不重复 reload」的判断，必须显式接 @input，不能让 Vue 生成默认的 input 处理器
  @Prop() readonly value?: number
  @Prop({ required: true }) readonly editable!: boolean
  // 新建分组（createAssetCategory）和拖动排序（reorderAssetCategories）都要带上 official（由 isOfficial
  // 从 scope 换算）与 scope/gameId 才能落对库；同 AssetUploadButton 一样不给默认值，逼父组件显式传
  // scope，避免 admin 在个人库操作时被后端兜底逻辑误落进官方库。book 传 scope='book'+gameId 时后端
  // 直接按此解析、不看 official（2026-09-18 本书素材库改造起 book 可写）；user 仍视同只读：
  // 父组件传入的 editable 已挡掉全部写入口，本组件不会被调用到写方法
  @Prop({ required: true }) readonly kind!: AssetKind
  @Prop({ required: true }) readonly scope!: AssetScope
  /** 本书 id：scope==='book' 时 AssetLibrary 传真实 game_id，其余 scope 传 0，随 createAssetCategory /
      reorderAssetCategories 的 scope 一并带上（isOfficial 的换算不变——book 不是官方，两个方法在
      scope==='book' 时 official 会被后端忽略，这里不单独分支，一律照常算好一起传） */
  @Prop({ default: 0 }) readonly gameId!: number

  /** 收起的分组 id（默认全部展开）；只存在内存里，切类型页签或换库时清空（§6.3） */
  collapsed: Record<number, boolean> = {}
  reordering = false

  moveVisible = false
  moveSource: AssetCategoryView | null = null
  // 换父目标：undefined = 未选择。初值必须放 data() 钩子——类属性初值为 undefined 会被
  // vue-class-component 丢弃、不进 data、不是响应式（同 AssetLibrary.categoryId 的既有坑，
  // 那里有完整注释）。本字段的「未选择」态恰好就是 undefined，而「确定」按钮的 :disabled
  // 直接绑在它上面，不响应式就永远禁用
  moveTargetId!: number | undefined
  moving = false

  // 见上方 moveTargetId 的注释：类属性初值为 undefined 会被 vue-class-component 丢弃，
  // 而 data() 返回对象的每个键都会被 defineReactive，undefined 值也不例外
  data(): { moveTargetId: number | undefined } {
    return { moveTargetId: undefined }
  }

  get isOfficial(): boolean {
    return this.scope === 'official'
  }

  get moveOptions(): ReparentTarget[] {
    const c = this.moveSource
    if (!c) return []
    // 弹窗开着时数据刷新，源分组从 categories 里消失（比如被另一个人删了）：候选要跟着清空，
    // 不能让 reparentTargets 拿一个不存在的 id 算出一份看着正常、实际全不对的列表
    const cats = this.categories || []
    if (!cats.some(x => x.id === c.id)) return []
    return reparentTargets(cats, c.id)
  }

  @Watch('kind')
  onKindChange() {
    this.collapsed = {}
  }

  @Watch('scope')
  onScopeChange() {
    this.collapsed = {}
  }

  selectAll() {
    if (this.value === undefined) return
    this.$emit('input', undefined)
  }

  selectUncategorized() {
    if (this.value === 0) return
    this.$emit('input', 0)
  }

  selectCategory(c: AssetCategoryView) {
    if (this.value === c.id) return
    this.$emit('input', c.id)
  }

  toggleExpanded(c: AssetCategoryView) {
    this.$set(this.collapsed, c.id, !this.collapsed[c.id])
  }

  handleCommand(c: AssetCategoryView, command: RowCommand) {
    if (command === 'child') this.handleCreate(c)
    else if (command === 'rename') this.handleRename(c)
    else if (command === 'move') this.openMove(c)
    else if (command === 'delete') this.handleDelete(c)
  }

  // 拖完把那一组兄弟的 id 按新顺序发给后端：后端按数组下标重写这几行的 sort（W9、spec §5.7）。
  // 失败时 emit changed 让父组件 reload，Branch 的 watch 会把顺序换回权威数据
  async onReorder(ids: number[]) {
    this.reordering = true
    try {
      const res = await reorderAssetCategories({
        kind: this.kind,
        ids,
        official: this.isOfficial,
        scope: this.scope,
        gameId: this.gameId > 0 ? this.gameId : null
      })
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '排序失败')
      }
      this.$emit('changed')
    } catch (e) {
      // 网络层错误已由 serviceGo 全局拦截器弹窗兜底，reload 把顺序还原
      this.$emit('changed')
    } finally {
      this.reordering = false
    }
  }

  async handleCreate(parent?: AssetCategoryView) {
    if (parent && !canAddChild(this.categories, parent.id)) {
      this.$message.error('当前服务暂不支持子分组，请更新服务后重试')
      return
    }
    try {
      let message = '分类名称'
      if (parent) {
        const path = categoryPathLabel(this.categories, parent.id)
        message = categoryIsLeaf(this.categories, parent.id) && parent.count > 0
          ? `在「${path}」下新建分组。「${parent.name}」现有的 ${parent.count} 个素材会自动移入新分组`
          : `在「${path}」下新建分组`
      }
      const res = await this.$prompt(message, parent ? '新增子分组' : '新建一级分组', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /\S/,
        inputErrorMessage: '名称不能为空'
      }) as MessageBoxInputData
      const name = res.value.trim()
      if (!name) return
      const cres = await createAssetCategory({
        kind: this.kind,
        name,
        official: this.isOfficial,
        parent_id: parent ? parent.id : null,
        scope: this.scope,
        gameId: this.gameId > 0 ? this.gameId : null
      })
      if (cres.data.code !== 0) {
        this.$message.error(cres.data.message || '创建失败')
        return
      }
      // 老后端会忽略 parent_id 却照样返回成功：结果不挂在父级下就报错，不能让作者以为建成了子分组；
      // data 整体缺失（畸形的 code === 0 响应）判不出挂对了父级，按同一分支处理，不能当检测通过
      if (parent && (!cres.data.data || cres.data.data.parent_id !== parent.id)) {
        this.$message.error('后端未升级，无法创建子分组，请刷新后检查')
        this.$emit('changed')
        return
      }
      this.$message.success('创建成功')
      if (parent) this.$set(this.collapsed, parent.id, false)
      this.$emit('changed')
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }

  async handleRename(c: AssetCategoryView) {
    try {
      const res = await this.$prompt('分类名称', '改名', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: c.name,
        inputPattern: /\S/,
        inputErrorMessage: '名称不能为空'
      }) as MessageBoxInputData
      const name = res.value.trim()
      if (!name || name === c.name) return
      const ures = await updateAssetCategory(c.id, { name })
      if (ures.data.code !== 0) {
        this.$message.error(ures.data.message || '改名失败')
        return
      }
      this.$message.success('改名成功')
      this.$emit('changed')
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }

  openMove(c: AssetCategoryView) {
    this.moveSource = c
    this.moveTargetId = undefined
    this.moveVisible = true
  }

  async confirmMove() {
    const c = this.moveSource
    if (!c || this.moveTargetId === undefined) return
    const parentId = this.moveTargetId === TOP_LEVEL_TARGET ? null : this.moveTargetId
    this.moving = true
    try {
      const res = await updateAssetCategory(c.id, { parent_id: parentId })
      // 后端还会拒绝成环 / 超过 5 级 / 目标分组里有素材，文案原样弹出（spec §5.3）
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '移动失败')
        return
      }
      this.moveVisible = false
      this.$message.success('移动成功')
      if (parentId !== null) this.$set(this.collapsed, parentId, false)
      this.$emit('changed')
    } catch (e) {
      // 网络层错误已由全局拦截器弹窗兜底；弹窗保持打开方便重试
    } finally {
      this.moving = false
    }
  }

  async handleDelete(c: AssetCategoryView) {
    const descendants = categorySubtreeIds(this.categories, c.id).length - 1
    try {
      await this.$confirm(
        descendants > 0
          ? `确定删除分类「${c.name}」？其下 ${descendants} 个子分组一并删除，所有素材移到未分类。`
          : `确定删除分类「${c.name}」？分类下的素材会移动到未分类。`,
        '确认删除',
        { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
      )
      const res = await deleteAssetCategory(c.id)
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '删除失败')
        return
      }
      this.$message.success('删除成功')
      // 删的是当前选中的分组或它的祖先时，选中态要跟着回到「全部」，不然会停在一个已经不存在的分类 id 上
      if (typeof this.value === 'number' && categorySubtreeIds(this.categories, c.id).includes(this.value)) {
        this.$emit('input', undefined)
      }
      this.$emit('changed')
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }
}
</script>

<style scoped lang="scss">
.asset-category-tree {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 4px 8px 12px;
}

.act-fixed-item {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 8px 12px;
  margin-bottom: 2px;
  border-radius: 6px;
  font-size: 13px;
  // 同 Branch 的 .act-item：行高写死 19px（分组行为了名字折行改的，见那边的注释），
  // 这两行跟着写，行高才跟分组行一样
  line-height: 19px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.06);
  }

  &.is-active {
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    font-weight: 600;

    .act-count {
      color: #6366f1;
    }
  }

  // 固定两行自己的名字 / 数量 / 占位图标：Branch 的 scoped 样式管不到这里，各写一份
  .act-drag-handle {
    flex-shrink: 0;
    margin-right: 4px;
    color: #9ca3af;
  }

  .act-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .act-count {
    flex-shrink: 0;
    margin-left: 8px;
    color: #9ca3af;
    font-size: 12px;
  }

  // 同 Branch：只留左右内边距，上下 2px 会把占位 span 撑到 23px、整行变高
  .act-edit-icon {
    flex-shrink: 0;
    margin-left: 6px;
    padding: 0 2px;
    color: #9ca3af;
  }
}

// 顶级分支的根元素（Branch 的 draggable）拿到本组件的 scope id，这条能作用到它
.act-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

// 只占位置的图标：跟真图标同一种元素、同一套字体，宽度天然一致，但永远不可见也点不到。
// 分类行的拖动手柄和操作图标平时是 opacity: 0——不可见却照样占着 flex 空间，
// 「全部 / 未分类」两行不补上等宽的占位，名字和数量就会跟分类行错开一截。
.is-space {
  visibility: hidden;
}

.act-create-bar {
  flex-shrink: 0;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid #ebeef5;
}

.act-create-btn {
  width: 100%;
  color: #6366f1;

  &:hover {
    color: #4f46e5;
  }
}

.act-move-title {
  margin-bottom: 10px;
  color: #4b5563;
  font-size: 13px;
}

.act-move-select {
  width: 100%;
}

.act-move-hint {
  margin-top: 8px;
  color: #9ca3af;
  font-size: 12px;
}
</style>
